import React, { useContext, useEffect, useState } from 'react';
import { ShopSettingsContext } from '../../../FootprintTech/AppController';
import { IErrorPanel } from './Interface';
import { Images } from '../../../assets/Images';
import styled, { createGlobalStyle } from 'styled-components';
import { ErrorComponent, i18n } from '@footprint/common-components';
import '@footprint/common-components/dist/index.css';
import RenderUtil from '../../../util/RenderUtil';
import { ModalEvents } from '../Modal';
import Spinner from '../../Button/Spinner';
import log from '../../../services/LogService';

// High-specificity CSS to override host site styles (e.g., Shopware)
// Using .fpt-modal as parent for increased specificity (works for both mobile and desktop)
const ErrorStyleOverrides = createGlobalStyle`
  .fpt-modal {
    /* Reset any inherited styles */
    box-sizing: border-box !important;
    font-family: 'Inter', sans-serif !important;

    /* Error Page Container */
    .fpt-c-error-page-container {
      align-items: center !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      display: flex !important;
      flex-direction: column !important;
      height: 100% !important;
      box-sizing: border-box !important;
    }

    /* Error Page */
    .fpt-c-error-page {
      padding-top: 0 !important;
      padding-left: 0.5rem !important;
      padding-right: 0.5rem !important;
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      height: 100% !important;
      min-height: 100% !important;
      width: 100% !important;
      box-sizing: border-box !important;
    }

    /* Blue Box Layout */
    .fpt-c-error-blue-box-layout {
      display: flex !important;
      flex-direction: column !important;
      justify-content: space-between !important;
      align-items: center !important;
      width: 100% !important;
      min-height: calc(100vh - 116px) !important;
      padding: 64px 12px 12px 12px !important;
      box-sizing: border-box !important;
    }

    /* Desktop adjustments */
    @media (min-width: 601px) {
      .fpt-c-error-blue-box-layout {
        min-height: auto !important;
        padding: 20px 12px 20px 12px !important;
        gap: 24px !important;
      }

      .fpt-c-error-content {
        align-items: center !important;
        text-align: center !important;
      }

      .fpt-c-error-title {
        text-align: center !important;
      }

      .fpt-c-error-body {
        text-align: center !important;
      }
    }

    /* Top Section */
    .fpt-c-error-top-section {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      gap: 32px !important;
      width: 100% !important;
    }

    /* Blue Box */
    .fpt-c-error-blue-box {
      position: relative !important;
      width: 345px !important;
      height: 240px !important;
      padding: 0 !important;
      background: #E0ECF8 !important;
      border-radius: 30px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      box-sizing: border-box !important;
    }

    .fpt-c-error-blue-box-illustration {
      max-width: 128px !important;
      max-height: 128px !important;
      width: 100% !important;
      height: auto !important;
      display: block !important;
      margin: 0 auto !important;
      object-fit: contain !important;
    }

    .fpt-c-error-blue-box-exclamation {
      position: absolute !important;
      top: 20px !important;
      left: 20px !important;
      width: 48px !important;
      height: 48px !important;
    }

    /* Content Area */
    .fpt-c-error-content {
      display: flex !important;
      flex-direction: column !important;
      align-items: flex-start !important;
      text-align: left !important;
      max-width: 345px !important;
      width: 100% !important;
      gap: 12px !important;
      padding: 0 !important;
      box-sizing: border-box !important;
    }

    /* Title */
    .fpt-c-error-title {
      font-family: 'Inter', sans-serif !important;
      font-size: 20px !important;
      font-weight: 700 !important;
      color: #000000 !important;
      margin: 0 !important;
      line-height: 28px !important;
      text-align: left !important;
    }

    /* Body Text */
    .fpt-c-error-body {
      font-family: 'Inter', sans-serif !important;
      font-size: 16px !important;
      font-weight: 500 !important;
      color: #000000 !important;
      margin: 0 !important;
      line-height: 24px !important;
      text-align: left !important;
    }

    /* Infobox Text */
    .fpt-c-error-infobox-text {
      font-family: 'Inter', sans-serif !important;
      font-size: 16px !important;
      font-weight: 500 !important;
      color: #000000 !important;
      margin: 0 !important;
      line-height: 24px !important;
      text-align: left !important;
    }

    /* Actions Bottom */
    .fpt-c-error-actions-bottom {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      width: 100% !important;
      max-width: 345px !important;
      gap: 12px !important;
      margin-top: 0 !important;
      padding: 0 !important;
      box-sizing: border-box !important;
    }

    /* Button Container */
    .fpt-c-actions-button-container {
      display: flex !important;
      align-content: center !important;
      justify-content: center !important;
      width: 100% !important;
      margin: 0 !important;
    }

    /* All Buttons in Error Actions */
    .fpt-c-error-actions-bottom .fpt-btn {
      font-family: 'Inter', sans-serif !important;
      font-size: 16px !important;
      font-weight: 500 !important;
      width: 345px !important;
      height: 56px !important;
      border-radius: 100px !important;
      padding: 0 !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      text-align: center !important;
      line-height: 1 !important;
      cursor: pointer !important;
      box-sizing: border-box !important;
      margin: 0 !important;
      text-decoration: none !important;
    }

    /* Primary Button */
    .fpt-c-error-actions-bottom .fpt-btn--wide-primary-round {
      background-color: rgb(30, 78, 95) !important;
      color: #ffffff !important;
      border: none !important;
    }

    /* Secondary/Link Button */
    .fpt-c-error-actions-bottom .fpt-btn--link-primary {
      background-color: transparent !important;
      color: rgb(30, 78, 95) !important;
      border: 1px solid rgb(30, 78, 95) !important;
      text-decoration: none !important;
    }

    /* Small screen adjustments */
    @media (max-width: 375px) {
      .fpt-c-error-blue-box-layout {
        padding: 40px 24px 16px 24px !important;
        justify-content: flex-start !important;
        gap: 20px !important;
      }

      .fpt-c-error-top-section {
        gap: 20px !important;
      }

      .fpt-c-error-blue-box {
        height: 200px !important;
        width: 100% !important;
        max-width: 345px !important;
      }

      .fpt-c-error-blue-box-illustration {
        max-width: 110px !important;
        max-height: 110px !important;
      }

      .fpt-c-error-title {
        font-size: 18px !important;
        line-height: 26px !important;
      }

      .fpt-c-error-body,
      .fpt-c-error-infobox-text {
        font-size: 14px !important;
        line-height: 22px !important;
      }

      .fpt-c-error-actions-bottom {
        margin-top: auto !important;
        padding-bottom: 8px !important;
      }
    }
  }
`;

// Styled components defined outside the component to avoid recreation on each render

// Mobile fullscreen container
const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  min-height: calc(var(--vh, 1vh) * 100);
  background-color: #fff;
  position: relative;
`;

// Desktop modal container
const DesktopOuterContainer = styled.div`
  width: 633px;
  padding: 25px;
  border-radius: 30px;
  background-color: #f8fcfd;
  margin: auto;
`;

const DesktopInnerContainer = styled.div`
  padding-bottom: 10px;
  border-radius: 20px;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.13);
  background-color: #fff;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 24px;
  background: none;
  border: none;
  font-size: 18px;
  color: var(--dark-slate-blue);
  cursor: pointer;
  width: 46px;
  z-index: 101;
`;

const DesktopCloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 18px;
  color: var(--dark-slate-blue);
  cursor: pointer;
  width: 46px;
  z-index: 3;
`;

const CloseIcon = styled.img``;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  height: 100%;

  &.fpt-show {
    display: flex;
  }

  &.fpt-hide {
    display: none;
  }
`;

const ErrorContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  &.fpt-show {
    display: flex;
  }

  &.fpt-hide {
    display: none;
  }
`;

export const ErrorPanelStandalone = (props: IErrorPanel) => {
  const {
    error,
    emitter,
  } = props;

  const shopSettings = useContext(ShopSettingsContext);
  const i18nNamespace = shopSettings.theme?.i18nNamespace;
  const initialLoadingState = !!i18nNamespace;
  log.info('custom translation detected:', initialLoadingState);
  const [ isLoading, setLoading ] = useState(initialLoadingState);

  const lang = shopSettings.lang || RenderUtil.getLang();

  // Handle viewport height for mobile browsers
  useEffect(() => {
    const handleResize = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    i18n.changeLanguage(lang);
    if (isLoading) {
      log.info('Loading custom translation', i18nNamespace);
      i18n.loadNamespaces(i18nNamespace)
        .then(() => {
          i18n.setDefaultNamespace(i18nNamespace);
          setLoading(false);
        })
        .catch(err => {
          log.error(err.message);
          setLoading(false);
        });
    }
  }, [isLoading]);

  const isMobile = RenderUtil.isMobile();

  // Mobile: fullscreen layout with CSS injection for host site override
  if (isMobile) {
    return (
      <>
        <ErrorStyleOverrides />
        <ErrorContainer>
          <CloseButton type="button" onClick={(ev) => {
            ev.nativeEvent.stopImmediatePropagation();
            emitter.emit(ModalEvents.onCloseModalPress);
          }}>
            <CloseIcon src={Images.close} width={34} />
          </CloseButton>
          <SpinnerContainer className={isLoading ? 'fpt-show' : 'fpt-hide'}>
            <Spinner />
          </SpinnerContainer>
          <ErrorContent className={isLoading ? 'fpt-hide' : 'fpt-show'}>
            <ErrorComponent
              code={error.errorCode}
              source='PDP'
              hideAction={false}
              emitter={emitter}
              showFeedbackLink={false}
            />
          </ErrorContent>
        </ErrorContainer>
      </>
    );
  }

  // Desktop: modal container layout
  return (
    <>
      <ErrorStyleOverrides />
      <DesktopOuterContainer>
        <DesktopInnerContainer>
          <DesktopCloseButton type="button" onClick={(ev) => {
            ev.nativeEvent.stopImmediatePropagation();
            emitter.emit(ModalEvents.onCloseModalPress);
          }}>
            <CloseIcon src={Images.close} width={34} />
          </DesktopCloseButton>
          <SpinnerContainer className={isLoading ? 'fpt-show' : 'fpt-hide'}>
            <Spinner />
          </SpinnerContainer>
          <ErrorContent className={isLoading ? 'fpt-hide' : 'fpt-show'}>
            <ErrorComponent
              code={error.errorCode}
              source='PDP'
              hideAction={true}
              emitter={emitter}
              showFeedbackLink={false}
            />
          </ErrorContent>
        </DesktopInnerContainer>
      </DesktopOuterContainer>
    </>
  );

};
